import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Plus, Search, ArrowDownToLine, ArrowUpFromLine, SlidersHorizontal, History, Edit2, Trash2, Wrench, Truck } from 'lucide-react';
import { miscAPI } from '../utils/api';
import useAuthStore from '../store/authStore';
import Modal from '../components/common/Modal';
import SectionHero from '../components/common/SectionHero';
import { fmt, apiError, today } from '../utils/helpers';
import useUrlQuery from '../utils/useUrlQuery';

const CATS = ['tools', 'equipment', 'spare_parts', 'packaging', 'consumables', 'other'];
const CAT_COLOR = { tools: '#fbbf24', equipment: '#38bdf8', spare_parts: '#a78bfa', packaging: '#34d399', consumables: '#f472b6', other: '#94a3b8' };

export default function MiscGoods() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { user, can } = useAuthStore();
  const canEdit = can('purchases.edit');
  const isAdmin = can('records.delete');
  const [items, setItems] = useState(null);
  const [q, setQ] = useState('');
  useUrlQuery(v => { setQ(v); setCat(''); });
  const [cat, setCat] = useState('');
  const [edit, setEdit] = useState(null);      // item form
  const [move, setMove] = useState(null);      // {item, type, quantity, note, move_date}
  const [hist, setHist] = useState(null);      // {item, rows}

  const load = () => miscAPI.list().then(r => setItems(r.data)).catch(() => { toast.error(t('error')); setItems([]); });
  useEffect(() => { load(); }, []); // eslint-disable-line

  const saveItem = async () => {
    if (!edit.name.trim()) return toast.error(t('fill_required'));
    try {
      if (edit.id) await miscAPI.update(edit.id, edit);
      else await miscAPI.create({ ...edit, quantity: parseFloat(edit.quantity) || 0 });
      toast.success(t('saved')); setEdit(null); load();
    } catch (e) { toast.error(apiError(e, t)); }
  };
  const saveMove = async () => {
    const qn = parseFloat(move.quantity);
    if (isNaN(qn) || qn < 0 || (move.type !== 'set' && qn <= 0)) return toast.error(t('fill_required'));
    try { await miscAPI.move(move.item.id, { type: move.type, quantity: qn, note: move.note || null, move_date: move.move_date }); toast.success(t('saved')); setMove(null); load(); }
    catch (e) { toast.error(apiError(e, t)); }
  };
  const openHist = async (item) => { try { setHist({ item, rows: (await miscAPI.movements(item.id)).data }); } catch (e) { toast.error(t('error')); } };
  const del = async (item) => { if (!window.confirm(t('confirm_delete'))) return; try { await miscAPI.delete(item.id); load(); } catch (e) { toast.error(apiError(e, t)); } };

  const ql = q.trim().toLowerCase();
  const list = (items || []).filter(i => (!cat || i.category === cat) && (!ql || [i.name, i.location, i.notes].some(v => (v || '').toLowerCase().includes(ql))));
  const onRoad = (items || []).filter(i => i.in_transit > 0).length;

  return (
    <div className="space-y-5">
      <SectionHero title={t('misc_goods')} subtitle={items ? <>{items.length} {t('misc_items_count')}{onRoad ? ` · ${onRoad} ${t('sh_on_the_road')}` : ''}</> : null}>
        {canEdit && <button className="btn btn-primary" onClick={() => setEdit({ name: '', category: 'tools', unit: lang === 'fa' ? 'عدد' : 'pcs', quantity: '', location: '', notes: '' })}><Plus size={16} /> {t('misc_add')}</button>}
      </SectionHero>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="mg-bar">
          <div className="sh-search"><Search size={14} /><input placeholder={t('search') + '...'} value={q} onChange={e => setQ(e.target.value)} /></div>
          <div className="mg-cats">
            <button className={!cat ? 'on' : ''} onClick={() => setCat('')}>{t('all')}</button>
            {CATS.map(c => <button key={c} className={cat === c ? 'on' : ''} style={{ '--c': CAT_COLOR[c] }} onClick={() => setCat(c)}>{t('misc_cat_' + c)}</button>)}
          </div>
        </div>
        {!items ? <div className="loading-center"><div className="spinner" /></div> : list.length === 0 ? (
          <div className="empty-state"><Wrench size={32} style={{ color: 'var(--text-muted)', margin: '0 auto 8px' }} /><p>{items.length ? t('no_data') : t('misc_empty')}</p></div>
        ) : (
          <div className="table-wrap"><table>
            <thead><tr><th>{t('name')}</th><th>{t('misc_category')}</th><th>{t('quantity')}</th><th className="hide-mobile">{t('misc_location')}</th><th className="hide-mobile">{t('misc_last_move')}</th>{canEdit && <th>{t('actions')}</th>}</tr></thead>
            <tbody>{list.map(i => (
              <tr key={i.id}>
                <td><div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{i.name}</div>{i.notes && <div className="text-xs truncate" style={{ color: 'var(--text-muted)', maxWidth: 240 }}>{i.notes}</div>}</td>
                <td><span className="mg-cat" style={{ '--c': CAT_COLOR[i.category] || CAT_COLOR.other }}>{t('misc_cat_' + i.category)}</span></td>
                <td><bdi style={{ fontWeight: 700, color: i.quantity > 0 ? 'var(--text-primary)' : 'var(--text-muted)' }}>{fmt(i.quantity)}</bdi> <span style={{ color: 'var(--text-muted)' }}>{i.unit}</span>
                  {i.in_transit > 0 && <Link to="/shipments" className="transit-chip"><Truck size={11} /> +<bdi>{fmt(i.in_transit)}</bdi> {t('sh_on_the_road_short')}</Link>}</td>
                <td className="hide-mobile">{i.location || '—'}</td>
                <td className="hide-mobile" style={{ color: 'var(--text-muted)' }}>{i.last_move || '—'}</td>
                {canEdit && <td><div className="flex gap-1">
                  <button className="btn btn-ghost btn-sm btn-icon" title={t('misc_in')} onClick={() => setMove({ item: i, type: 'in', quantity: '', note: '', move_date: today() })}><ArrowDownToLine size={14} style={{ color: '#34d399' }} /></button>
                  <button className="btn btn-ghost btn-sm btn-icon" title={t('misc_out')} onClick={() => setMove({ item: i, type: 'out', quantity: '', note: '', move_date: today() })}><ArrowUpFromLine size={14} style={{ color: '#f87171' }} /></button>
                  <button className="btn btn-ghost btn-sm btn-icon" title={t('misc_set')} onClick={() => setMove({ item: i, type: 'set', quantity: i.quantity, note: '', move_date: today() })}><SlidersHorizontal size={14} /></button>
                  <button className="btn btn-ghost btn-sm btn-icon" title={t('kardex')} onClick={() => openHist(i)}><History size={14} /></button>
                  <button className="btn btn-ghost btn-sm btn-icon" title={t('edit')} onClick={() => setEdit({ ...i })}><Edit2 size={14} /></button>
                  {isAdmin && <button className="btn btn-ghost btn-sm btn-icon" title={t('delete')} onClick={() => del(i)}><Trash2 size={14} /></button>}
                </div></td>}
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>

      {edit && (
        <Modal title={edit.id ? t('edit') : t('misc_add')} onClose={() => setEdit(null)}
          footer={<><button className="btn btn-secondary" onClick={() => setEdit(null)}>{t('cancel')}</button><button className="btn btn-primary" onClick={saveItem}>{t('save')}</button></>}>
          <div className="form-group"><label className="form-label">{t('name')} *</label><input className="input" value={edit.name} onChange={e => setEdit(x => ({ ...x, name: e.target.value }))} /></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t('misc_category')}</label>
              <select className="select" value={edit.category} onChange={e => setEdit(x => ({ ...x, category: e.target.value }))}>{CATS.map(c => <option key={c} value={c}>{t('misc_cat_' + c)}</option>)}</select></div>
            <div className="form-group"><label className="form-label">{t('unit')}</label><input className="input" value={edit.unit} onChange={e => setEdit(x => ({ ...x, unit: e.target.value }))} /></div>
          </div>
          <div className="form-row">
            {!edit.id && <div className="form-group"><label className="form-label">{t('misc_opening_qty')}</label><input className="input" type="number" min="0" step="any" value={edit.quantity} onChange={e => setEdit(x => ({ ...x, quantity: e.target.value }))} /></div>}
            <div className="form-group"><label className="form-label">{t('misc_location')}</label><input className="input" value={edit.location || ''} onChange={e => setEdit(x => ({ ...x, location: e.target.value }))} /></div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">{t('notes')}</label><textarea className="input" style={{ minHeight: 56 }} value={edit.notes || ''} onChange={e => setEdit(x => ({ ...x, notes: e.target.value }))} /></div>
        </Modal>
      )}

      {move && (
        <Modal title={`${t('misc_' + move.type)} — ${move.item.name}`} onClose={() => setMove(null)}
          footer={<><button className="btn btn-secondary" onClick={() => setMove(null)}>{t('cancel')}</button><button className="btn btn-primary" onClick={saveMove}>{t('save')}</button></>}>
          <p className="text-xs" style={{ color: 'var(--text-muted)', marginBottom: 12 }}>{t('misc_now')}: <bdi>{fmt(move.item.quantity)} {move.item.unit}</bdi></p>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{move.type === 'set' ? t('misc_counted_qty') : t('quantity')}</label>
              <input className="input" type="number" min="0" step="any" autoFocus value={move.quantity} onChange={e => setMove(x => ({ ...x, quantity: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">{t('date')}</label><input className="input" type="date" value={move.move_date} onChange={e => setMove(x => ({ ...x, move_date: e.target.value }))} /></div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">{move.type === 'out' ? t('misc_out_to') : t('notes')}</label><input className="input" value={move.note} onChange={e => setMove(x => ({ ...x, note: e.target.value }))} /></div>
        </Modal>
      )}

      {hist && (
        <Modal title={`${t('kardex')} — ${hist.item.name}`} size="modal-lg" onClose={() => setHist(null)}>
          {hist.rows.length === 0 ? <p className="sh-empty">{t('no_data')}</p> : (
            <div className="table-wrap"><table>
              <thead><tr><th>{t('date')}</th><th>{t('misc_move_type')}</th><th>{t('quantity')}</th><th>{t('misc_balance')}</th><th>{t('notes')}</th></tr></thead>
              <tbody>{hist.rows.map(r => (
                <tr key={r.id}>
                  <td>{r.move_date}</td>
                  <td>{t('misc_mt_' + r.move_type)}{r.ref_label && <div className="text-xs" style={{ color: 'var(--text-muted)' }}><bdi>{r.ref_label}</bdi></div>}</td>
                  <td><bdi style={{ color: r.qty_change >= 0 ? '#34d399' : '#f87171', fontWeight: 700 }}>{r.qty_change >= 0 ? '+' : '−'}{fmt(Math.abs(r.qty_change))}</bdi></td>
                  <td><bdi>{fmt(r.balance_after)}</bdi></td>
                  <td className="text-xs">{r.note}{r.user && <div style={{ color: 'var(--text-muted)' }}>{r.user}</div>}</td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </Modal>
      )}
    </div>
  );
}
